import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { API, getToken } from "@/lib/api";
import axios from "axios";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { CameraScanner } from "@/components/CameraScanner";
import { Gavel, Upload, Camera, Loader2, Copy, ShieldAlert, Scale, Clock, ExternalLink, FileText } from "lucide-react";

export default function DenialFighter() {
  const { t, lang } = useI18n();
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const analyze = async (submitFiles) => {
    const useFiles = submitFiles || files;
    if (!useFiles.length && text.trim().length < 40) { toast.error(t("denial_need_input")); return; }
    setLoading(true); setResult(null);
    const fd = new FormData();
    useFiles.forEach((f) => fd.append("files", f));
    if (text.trim()) fd.append("text", text.trim());
    fd.append("language", lang);
    try {
      const r = await axios.post(`${API}/denial-fighter`, fd, { headers: { Authorization: `Bearer ${getToken()}` } });
      setResult(r.data.analysis);
      setScanOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("denial_failed"));
    } finally { setLoading(false); }
  };

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success(t("copied")); };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Gavel className="h-7 w-7 text-brandblue" /> {t("denial_title")}</h1>
      <p className="mt-1 text-slate-500 max-w-2xl">{t("denial_intro")}</p>

      {!result && (
        <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="denial-upload" onClick={() => fileRef.current?.click()}
              className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 py-6 hover:border-brandblue transition-colors">
              <Upload className="h-6 w-6 text-brandblue" /><span className="text-sm font-medium text-navy">{t("upload_file")}</span>
              <span className="text-xs text-slate-400">JPG / PNG / PDF</span>
            </button>
            <button data-testid="denial-camera" onClick={() => setScanOpen(true)}
              className="flex flex-col items-center gap-2 rounded-2xl border-2 border-brandteal/40 bg-brandteal/5 py-6 hover:bg-brandteal/10 transition-colors">
              <Camera className="h-6 w-6 text-brandteal" /><span className="text-sm font-medium text-navy">{t("scan_photo")}</span>
            </button>
          </div>
          {files.length > 0 && (
            <p className="mt-3 text-sm text-covered flex items-center gap-1.5" data-testid="denial-files"><FileText className="h-4 w-4" /> {files.length} {t("file_ready")}</p>
          )}
          <p className="mt-4 mb-1 text-sm text-slate-400">{t("or_paste_denial")}</p>
          <textarea data-testid="denial-text" value={text} onChange={(e) => setText(e.target.value)} rows={5}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            placeholder={t("paste_denial")} />
          <Button data-testid="denial-analyze" onClick={() => analyze()} disabled={loading}
            className="mt-4 w-full rounded-full brand-gradient text-white py-6 font-semibold">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> {t("denial_working")}</> : t("denial_analyze")}
          </Button>
          {loading && <p className="mt-2 text-xs text-slate-400 text-center">{t("denial_working_hint")}</p>}
          <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp" multiple
            onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-5 max-w-3xl" data-testid="denial-result">
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy"><ShieldAlert className="h-5 w-5 text-amber-500" /> {t("why_denied")}</h2>
            <p className="mt-2 text-navy leading-relaxed">{result.denial_reason}</p>
            {result.is_it_fair && (
              <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 p-3">
                <Scale className="h-4 w-4 mt-0.5 text-brandblue shrink-0" /><p className="text-sm text-slate-600">{result.is_it_fair}</p>
              </div>
            )}
            {result.deadline_note && (
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700"><Clock className="h-4 w-4" /> {result.deadline_note}</div>
            )}
          </div>

          {result.your_rights?.length > 0 && (
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="text-lg font-semibold text-navy">{t("your_rights")}</h2>
              <ul className="mt-3 space-y-2">
                {result.your_rights.map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-navy"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brandblue shrink-0" />{r}</li>)}
              </ul>
            </div>
          )}

          {result.appeal_letter && (
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-navy">{t("your_appeal_letter")}</h2>
                <Button data-testid="copy-appeal" variant="outline" className="rounded-full h-9" onClick={() => copy(result.appeal_letter)}><Copy className="h-4 w-4 mr-1" />{t("copy")}</Button>
              </div>
              <pre className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-navy font-sans leading-relaxed">{result.appeal_letter}</pre>
            </div>
          )}

          {result.sources?.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-5">
              <p className="text-sm font-semibold text-slate-500 mb-2">{t("sources")}</p>
              <div className="flex flex-wrap gap-2">
                {result.sources.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-brandblue hover:bg-blue-100">
                    <ExternalLink className="h-3 w-3" />{s.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          <Button variant="outline" onClick={() => { setResult(null); setFiles([]); setText(""); }} className="rounded-full">{t("denial_start_over")}</Button>
        </div>
      )}

      <CameraScanner open={scanOpen} onClose={() => setScanOpen(false)} onSubmit={(f) => analyze(f)} submitting={loading} readingLabel={t("denial_working")} />
    </AppLayout>
  );
}
